import axiosSearch from '@/plugins/axios-search';
import { ForumLink } from '@/models/ForumLink';
import { AxiosResponse } from 'axios';
import { logError } from '@/utils/logger';
import BCXHTMLSanitizer from '@/utils/BCXHTMLSanitizer';
import ForumService from '@/services/ForumService';
import { GROUP_ID_BUG, GROUP_ID_FEATURE, GROUP_ID_FEEDBACK } from '@/views/forum/models/Forum';
import { ParticipationType } from '@/models/ParticipationType';

interface ParticipationThreadLink {
  forumId: string;
  groupId: string;
  threadId: string;
}

const DEFAULT_PARTICIPATION_FORUM_ID = '74502bb7-1b2e-4c42-9454-e48d0dee6220';

function getGroupId(participationType: ParticipationType): string {
  switch (participationType) {
    case 'BUG':
      return GROUP_ID_BUG;
    case 'FEATURE_REQUEST':
      return GROUP_ID_FEATURE;
    case 'FEEDBACK':
      return GROUP_ID_FEEDBACK;
    default:
      return GROUP_ID_FEEDBACK;
  }
}

async function resolveForumId(groupId: string): Promise<string> {
  try {
    const forumList = await ForumService.getForumList();
    const matchingForum = forumList.find((forum) => forum.groups?.some((group) => group.groupId === groupId));
    if (matchingForum?.forumId) {
      return matchingForum.forumId;
    }
  } catch (error) {
    logError(error);
  }

  return DEFAULT_PARTICIPATION_FORUM_ID;
}

export default {
  async getMatchingTopics(newTitle:string, participationType:string):Promise<ForumLink[]> {
    const response:AxiosResponse = await axiosSearch.post('/v1/participation/search', {
      newTitle,
      participationType,
    }).catch((err) => logError(err)) as AxiosResponse;
    return response?.data ?? [] as ForumLink[];
  },

  async addParticipationTopic(title:string, participationType:ParticipationType, content:string):Promise<ParticipationThreadLink> {
    const groupId = getGroupId(participationType);
    const forumId = await resolveForumId(groupId);
    const forum = await ForumService.createThread(
      forumId,
      groupId,
      BCXHTMLSanitizer.beforeSave(title),
      BCXHTMLSanitizer.beforeSave(content),
      [],
    );

    const [createdThread] = forum.groups?.[0]?.threads ?? [];
    if (!createdThread?.threadId) {
      throw new Error('Created thread missing in participation response');
    }

    return {
      forumId: createdThread.forumId || forumId,
      groupId: createdThread.groupId || groupId,
      threadId: createdThread.threadId,
    };
  },
};
